import { useFeatureGate } from '@/features/billing/gates';
import { Role } from '@/types/gql.generated';

type CollaboratorWithRole = { role: Role };

/**
 * How many more collaborators may be invited.
 *
 * The allowance comes from the `collaborators` gate, which resolves it per
 * experience type — planners allow fewer on free than pages do — and reads the
 * *experience's* entitlement rather than the inviting user's, since the owner is
 * the one who has to keep those collaborators afterwards. Used for schedules too,
 * where there is no experience in context and the gate falls back to its default
 * rule.
 *
 * `hasReachedLimit` and `isUpgradable` are deliberately separate: a paid workspace
 * at the entitled cap has reached the limit but has nothing to buy, so the caller
 * must not offer it an upgrade. Inviting itself is never withheld — a collaborator
 * who can change and remove people can add them too; CollaboratorsSection withholds
 * only the CTA, since the seats belong to the experience's home org.
 */
export const useCollaboratorLimit = (collaborators: CollaboratorWithRole[]) => {
  const nonOwnerCount = collaborators.filter(
    (collaborator) => collaborator.role !== Role.Owner
  ).length;

  const {
    isGated,
    isLocked,
    limit,
    hasReachedLimit,
    isAllowanceUpgradable,
    promptUpgrade,
  } = useFeatureGate('collaborators', { count: nonOwnerCount });

  // A rule with no entitled cap resolves to Infinity, which must not reach the UI as
  // a number to render or slice with. `null` says "no cap" explicitly instead.
  const isFiniteLimit = limit != null && Number.isFinite(limit);

  return {
    remainingSlots: isFiniteLimit ? Math.max(0, limit - nonOwnerCount) : null,
    // Locked covers the loading window, so the invite form isn't briefly open to
    // someone who turns out to be over their allowance.
    hasReachedLimit: hasReachedLimit || isLocked,
    /**
     * A bigger plan would raise the cap — false at the paid cap. Offered from the
     * first slot rather than only once the allowance is spent, so an owner sees the
     * ceiling before running into it. `isGated` stays in the expression to cover a
     * future rule that carries no allowance, where there is no ceiling to advertise
     * and being at the cap is the only thing left to report.
     */
    isUpgradable: isGated || isAllowanceUpgradable,
    promptUpgrade,
  };
};
